import { api } from '@/lib/api'
import { getUserLocale } from '@/lib/locale'
import type { Accrual, AccrualPayload, PostingStatus } from './types'

export type PostScope =
  | 'all'
  | 'bank'
  | 'accruals'
  | 'depreciation'
  | 'prepaid'
  | 'loan'
  | 'journal'

export type PostSource =
  | 'bank'
  | 'accruals'
  | 'journal'
  | 'depreciation'
  | 'prepaid'
  | 'loan'
  | 'invoices'
  | 'bills'

export interface PostSelectedResult {
  message?: string
  posted?: number
  skipped?: number
  /** AR side only - invoices that matched an approval policy and were
   *  routed to the inbox instead of being posted to the GL. The post
   *  fires automatically once the approval transitions to approved.
   *  Counted separately so the toast can tell the user accurately. */
  pendingApproval?: number
  source?: string
}

export const accrualsApi = {
  list: () => api.get<Accrual[]>('/api/accruals/'),
  saveAll: (rows: AccrualPayload[]) =>
    api.post<{ message?: string }>('/api/accruals/', rows),
  remove: (id: number) => api.del<{ message?: string }>(`/api/accruals/${id}`),
  postingStatus: () => api.get<PostingStatus>('/api/reporting/posting-status'),
  postTransactions: (scope: PostScope = 'all', fiscalStartMonth?: number) =>
    api.post<{ message?: string }>('/api/reporting/post-transactions', {
      fiscalStartMonth: fiscalStartMonth ?? getUserLocale().fiscalStartMonth,
      scope,
    }),
  /**
   * Selectively post a user-chosen subset of one source's unposted
   * entries. Backs the "Post Now" banner on every page that posts to
   * the GL - only the ticked rows are sent, one source per request, so
   * posting accruals never drags in journal entries (and vice versa).
   */
  postSelected: (source: PostSource, ids: number[], fiscalStartMonth?: number) =>
    api.post<PostSelectedResult>('/api/reporting/post-selected', {
      source,
      ids,
      fiscalStartMonth: fiscalStartMonth ?? getUserLocale().fiscalStartMonth,
    }),
}
